import classNames from "classnames/bind";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import {
  BaseButton,
  Card,
  UnsavedChangesPrompt,
} from "@velocity-ehs/component-library";
import FormSkeleton from "../../sharedComponents/FormSkeleton";
import { Input } from "../../sharedComponents/inputs/Input";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import { updateOrgUnit } from "../../supportFunctions/graphql/HierarchiesQueries";
import { OrgUnit } from "./Hierarchies";
import classes from "./OrgUnitProfile.module.scss";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import GatewayService from "../../../services/GatewayService";

type OrgUnitProfileProps = {
  orgUnit?: OrgUnit;
  onUpdate?: (orgUnit: OrgUnit) => void;
  isLoading: boolean;
};

const OrgUnitProfile = ({
  orgUnit,
  onUpdate,
  isLoading,
}: OrgUnitProfileProps): React.ReactElement => {
  const cx = classNames.bind(classes);
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  const history = useHistory();

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<{ name: string }>({ mode: "onBlur" });

  const onSubmit = async (data: { name: string }) => {
    try {
      if (orgUnit) {
        const response = await GatewayService.api.post(
          "",
          updateOrgUnit(orgUnit.id, data.name)
        );
        if (showSnackbar) {
          showSnackbar(
            SnackbarType.Success,
            t("org_unit_updated_successfully")
          );
        }
        const updatedOrg = response.data.data.updateOrganizationalUnit;
        if (onUpdate) onUpdate(updatedOrg);
      }
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
  };

  useEffect(() => {
    reset(orgUnit);
  }, [orgUnit, reset]);

  return (
    <>
      <Card title={t("meta_org_unit_profile")}>
        {isLoading ? (
          <FormSkeleton rows={1} />
        ) : (
          <>
            {orgUnit?.lineage ? (
              <div className={cx(classes.lineage)}>
                <p className={cx(classes.label)}>
                  {t("meta_org_unit_parents")}
                </p>
                <p>{orgUnit.lineage}</p>
              </div>
            ) : null}
            <form id="editOrgUnit" onSubmit={handleSubmit(onSubmit)}>
              <Input
                control={control}
                name="name"
                label={t("universal_Name")}
                rules={{
                  required: t("customer_Form_Name_Required"),
                  maxLength: {
                    value: 25,
                    message: t(
                      "hierarchies_Add_Org_Unit_Name_Max_Length_Validation"
                    ),
                  },
                  pattern: {
                    value: /^\s*[^>]+\s*$/i,
                    message: `${t("message_name_cannot_contain")} >.`,
                  },
                }}
                errorMessage={errors ? errors.name?.message : undefined}
              />
              <BaseButton
                loading={isSubmitting}
                form="editOrgUnit"
                type="submit"
                label={t("universal_Save")}
                className={cx(classes.button)}
              />
            </form>
          </>
        )}
      </Card>
      <UnsavedChangesPrompt isDirty={isDirty} history={history} />
    </>
  );
};

export default OrgUnitProfile;
